<template>
    <LayoutBase class="home">
        <van-sticky>
            <div class="search-container">
                <van-search
                    class="search-body"
                    v-model="searchValue"
                    shape="round"
                    clearable
                    :placeholder="$t('home.searchPlaceholder')"
                    @search="searchClick"
                    @clear="clearSearch"
                >
                    <template #right-icon>
                        <div class="btn-search" @click="searchClick" v-if="searchValue">
                            {{ $t("home.searchButton") }}
                        </div>
                    </template>
                </van-search>
                <div class="btn-filter">
                    <van-icon
                        :class-prefix="$constants.CUSTOM_ICON_PREFIX"
                        name="filter"
                        :color="filterColor"
                        @click="clickFilter"
                    />
                </div>
            </div>
        </van-sticky>
        <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
            <div class="container">
                <van-list
                    v-model="loading"
                    :finished="finished"
                    :finished-text="finishText"
                    @load="onLoad"
                    :offset="10"
                    :immediate-check="false"
                >
                    <PlanCard
                        v-for="item in planList"
                        :plan-item="item"
                        :org-config="orgConfig"
                        :key="item.id"
                        @click="$router.push('/plan/' + item.id)"
                    />
                </van-list>
                <van-empty :description="$t('common.nodata')" v-if="!planList.length > 0 && finished" />
            </div>
        </van-pull-refresh>
        <PlanFilter
            :show="isPlanFilterShow"
            :show-sort="true"
            @confirm="confirmFilter"
            @closed="closed"
            :org-config="orgConfig"
        />
        <TabbarBase />
    </LayoutBase>
</template>
<script>
import LayoutBase from "@/components/layouts/LayoutBase.vue";
import TabbarBase from "@/components/TabbarBase.vue";
import Vue from "vue";
import _ from "lodash";
import PlanCard from "@/views/home/PlanCard.vue";
import { datetimeFormat, moneyFormat } from "@/utils/lang";
import PlanFilter from "@/views/home/PlanFilter.vue";
import { plans } from "@/api/home";
import { createNamespacedHelpers } from "vuex";

const { mapGetters, mapActions } = createNamespacedHelpers("config");

export default Vue.extend({
    name: "Home",
    components: { LayoutBase, PlanCard, TabbarBase, PlanFilter },
    props: {
        keyWord: String,
        sortKey: String
    },
    data() {
        return {
            searchValue: "",
            loading: false,
            finished: false,
            refreshing: false,
            isPlanFilterShow: false,
            planList: new Array(0),
            total: 0,
            finishText: this.$t("home.finishedText")
        };
    },
    created() {
        window.scroll(0, 0);
        this.searchValue = this.keyWord;
        if (this.sortKey) {
            this.$store.dispatch("home/sortKey", { filterValue: this.sortKey });
        }
        this.searchClick();
    },
    computed: {
        ...mapGetters(["orgConfig"]),
        buildParams() {
            let params = this.$store.getters["home/pagination"];
            params = _.merge({}, params, this.$store.getters["home/searchForm"]);
            return params;
        },
        filterColor() {
            if (this.$store.getters["home/isEmptySearch"]) {
                return "#40444a";
            }
            return "var(--primary-brand-color)";
        }
    },
    methods: {
        clickFilter() {
            this.isPlanFilterShow = !this.isPlanFilterShow;
        },
        clearSearch() {
            this.$store.dispatch("home/keyword", { filterValue: "" });
            this.initQuery();
        },
        initQuery() {
            this.isPlanFilterShow = false;
            this.finished = false;
            this.planList = [];
            this.$store.dispatch("home/resetPage");
            this.doQuery();
        },
        confirmFilter() {
            this.isPlanFilterShow = false;
        },
        closed() {
            this.initQuery();
        },
        onLoad() {
            if (this.refreshing) {
                this.planList = [];
                this.refreshing = false;
            }
            this.doQuery();
        },
        doQuery() {
            this.finishText = this.$t("home.finishedText");
            this.loading = true;
            plans(this.buildParams)
                .then(resp => {
                    if (resp.data) {
                        const pageData = resp.data.data;
                        this.total = resp.data.page.totalElements;
                        if (_.isEmpty(pageData) && _.isEmpty(this.planList)) {
                            this.finishText = "";
                        }
                        if (_.isEmpty(pageData)) {
                            this.finished = true;
                            return;
                        }
                        this.planList = _.concat(this.planList, this.mapperPlanList(pageData));
                        if (this.planList.length >= this.total) {
                            this.finished = true;
                        }
                        this.$store.dispatch("home/nextPage");
                    }
                })
                .catch(() => {
                    this.finishText = "";
                    this.finished = true;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        onRefresh() {
            this.finished = false;
            this.$store.dispatch("home/resetPage");
            this.onLoad();
        },
        searchClick() {
            this.$store.dispatch("home/keyword", { filterValue: this.searchValue });
            this.initQuery();
        },
        mapperPlanList(respData) {
            return _.map(respData, data => {
                let coverPath = "";
                if (data.introduction.cover) {
                    coverPath = data.introduction.cover.thumbnailUrl;
                }
                const setting = data.setting;
                const occupationInfo = data.program.occupationInfo;
                return {
                    id: data.id,
                    planName: data.planName,
                    registrationCount: data.registrationCount,
                    levelName: this.$t("common.levelEnum." + occupationInfo.occupationLevel),
                    level: occupationInfo.occupationLevel,
                    occupationName: occupationInfo.occupationName,
                    classificationName: occupationInfo.classificationName,
                    timeRange:
                        datetimeFormat(setting.startTime, this.$constants.DATE_FMT) +
                        " - " +
                        datetimeFormat(setting.endTime, this.$constants.DATE_FMT),
                    registrationAmount: moneyFormat(setting.fee),
                    registrationEnrollmentCount: data.registrationCount + "/" + setting.enrollmentCount,
                    registrationStatus: setting.registrationStatus,
                    certificateType: setting.certificateType,
                    coverPath: coverPath || require("@/assets/plan-default-cover.png"),
                    orgName: data.org.name
                };
            });
        },
        ...mapActions(["getOrgConfig"])
    },
    async mounted() {
        await this.getOrgConfig();
    }
});
</script>

<style lang="less" scoped>
@import "../../../styles/vars.less";

.search-container {
    display: flex;
    align-items: center;
    border-bottom: 1px solid @border-color;
    background-color: #ffffff;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.05);

    .search-body.van-search {
        flex: 1;
    }

    .btn-search {
        width: 58px;
        border-radius: 16px;
        background-color: @btn-info-color;
        color: #ffffff;
        text-align: center;
    }

    /deep/ .icomoon-filter {
        font-size: 24px !important;
    }

    .btn-filter {
        margin: 0 12px 0 0;

        i {
            vertical-align: middle;
        }
    }
}

.home {
    .container {
        min-height: 100vh;
        background-color: @app-background-color;
        padding-bottom: 56px;

        .swipe {
            border-radius: 6px;
            margin: 12px 16px;

            .van-swipe__track .van-swipe-item {
                border-radius: 6px;
                height: 157px;
            }

            .swipe-image {
                height: 157px;
                width: 100%;
                object-fit: cover;
            }
        }
    }
}
</style>
